import _ from 'lodash'
import { injectNodeId } from './treeHelper'
import treeMixin from './treeMixin'
import { isVueComponent } from '../../core/utils/KVdom'

export default {
  name: 'KTree',
  mixins: [treeMixin],
  provide() {
    return {
      tree: this,
    }
  },
  props: {
    // 数据
    data: {
      type: Array,
      required: true,
      // `[]`
      default() {
        return []
      },
    },
    // 显示连接线
    showLine: {
      type: Boolean,
      default: false,
    },
    // 节点唯一标识，全局唯一
    nodeKey: {
      type: String,
      default: '',
    },
    // 默认展开节点的keys
    expandedKeys: {
      type: Array,
      default: null,
    },
    // 默认选中节点的key（只支持单选）
    selectedKey: {
      type: [String, Number],
      default: null,
    },
    // 默认选中节点的keys
    checkedKeys: {
      type: Array,
      default: null,
    },
    // 是否可以点击树节点选中：与checkable互斥，优先读取checkable状态
    selectable: {
      type: Boolean,
      default: true,
    },
    // 是否可以使用选择框进行选择：与selectable互斥，优先读取checkable状态
    checkable: {
      type: Boolean,
      default: false,
    },
    // check操作严格模式（父子选中不关联）
    checkStrictly: {
      type: Boolean,
      default: false,
    },
    // 选中行时自动check节点（只有在checkable的模式时有效）
    checkOnSelect: {
      type: Boolean,
      default: true,
    },
    // 默认展开所有节点
    expandAll: {
      type: Boolean,
      default: false,
    },
    // 展开默认selected的Node
    defaultExpandSelected: {
      type: Boolean,
      default: true,
    },
    // 展开默认checked的Node
    defaultExpandChecked: {
      type: Boolean,
      default: true,
    },
    // 层级缩进单位宽度,最终padding-left值为数的层级*缩进单位宽度
    indentUnitWidth: {
      type: Number,
      default: 16,
    },
  },
  data() {
    return {
      level: 0,
      // 当前选中的数据
      selectedData: null,
      // 当前check的数据
      checkedData: [],
      // check模式下当前激活的Node
      activeIndex: null,
      // 非用户触发的check事件（无需emit事件）
      triggerCheckBySelf: false,
      // 非用户触发的select事件（无需emit事件）
      triggerSelectBySelf: false,
    }
  },
  watch: {
    data: {
      handler() {
        injectNodeId(this.data, this.nodeKey)
        // if (this.nodeKey) {
        //   if (this.defaultCheckedKeys && this.defaultCheckedKeys.length > 0) {
        //     this.setDefaultCheckedData(this.data)
        //   }
        // }
      },
      immediate: true,
    },
    selectedData() {
      if (this.triggerSelectBySelf) {
        this.triggerSelectBySelf = false
        return
      }
      // 节点select事件
      // @arg 激活节点Data item对象
      this.$emit('select', this.selectedData)
    },
    checkedData() {
      if (this.triggerCheckBySelf) {
        this.triggerCheckBySelf = false
        return
      }
      // 节点check事件
      // @arg check节点Data item数组
      this.$emit('check', this.checkedData)
    },
    checkable: {
      handler(val) {
        if (val) {
          // checkable 和 selectable 是互斥的，且优先checkable
          this.selectedData = null
        }
      },
      immediate: true,
    },
  },
  methods: {
    hasNodeKey() {
      if (!this.nodeKey) {
        console.warn('missing nodeKey props')
        return false
      }
      return true
    },
    /**
     * 获取Node
     * @param target 节点的key/data/node
     * @returns {*}
     */
    getNode(target) {
      let node
      if (isVueComponent(target)) {
        node = target
      } else {
        const key = _.isObject(target) ? target[this.nodeKey] : target
        const nodes = this.getNodesByKeys(this, [key])
        if (nodes.length > 0) {
          node = nodes[0]
        }
      }
      return node
    },
    /**
     * 使用keys匹配tree中的所有data
     * @param data
     * @param keys
     * @param result
     * @returns {*}
     */
    getDataByKeys(data, keys = [], result = []) {
      if (!this.hasNodeKey()) {
        return []
      }
      if (_.isEmpty(keys)) {
        return []
      }
      _.each(data, (item) => {
        if (keys.indexOf(item[this.nodeKey]) !== -1) {
          result.push(item)
        }
        if (!_.isEmpty(item.children)) {
          this.getDataByKeys(item.children, keys, result)
        }
      })
      return result
    },
    /**
     * 使用keys获取tree中的所有nodes
     * @param node
     * @param keys
     * @param result
     */
    getNodesByKeys(node, keys = [], result = []) {
      if (!this.hasNodeKey()) {
        return []
      }
      if (_.isEmpty(keys)) {
        return []
      }
      const children = this.getChildren(node)
      _.each(children, (child) => {
        if (keys.indexOf(child.data[this.nodeKey]) !== -1) {
          result.push(child)
        }
        if (child.hasChildren) {
          this.getNodesByKeys(child, keys, result)
        }
      })
      return result
    },
    /**
     * 获取选中节点
     * @param leafOnly Boolean: true表示只返回叶子节点
     */
    getCheckedNodes(leafOnly) {
      const keys = this.getCheckedKeys(leafOnly)
      return this.getNodesByKeys(this, keys)
    },

    /**
     * @vuese
     * 获取选中节点的data
     * @arg leafOnly Boolean: true表示只返回叶子节点
     */
    getCheckedData(leafOnly) {
      return leafOnly
        ? _.filter(this.checkedData, (item) => _.isEmpty(item.children))
        : this.checkedData
    },

    /**
     * @vuese
     * 获取选中节点的keys
     * @param leafOnly Boolean: true表示只返回叶子节点的keys
     */
    getCheckedKeys(leafOnly) {
      const checkedData = leafOnly
        ? _.filter(this.checkedData, (item) => _.isEmpty(item.children))
        : this.checkedData
      if (!this.hasNodeKey()) {
        return []
      }
      return _.map(checkedData, (item) => item[this.nodeKey])
    },

    /**
     * @vuese
     * 使用keys选中节点
     * @param keys Array: 待选中节点的keys
     * @param options Object: expand 展开节点，默认false，展开节点时默认会展开其所有父节点；expandParent 展开父节点，默认false
     */
    setCheckedKeys(keys, options = { expand: false, expandParent: false }) {
      if (!this.hasNodeKey()) {
        return
      }
      this.checkedData = this.getDataByKeys(this.data, keys)
      const nodes = this.getNodesByKeys(this, keys)
      _.each(nodes, (node) => {
        node.traverse()
        if (options.expand) {
          node.expandNode()
        }
        if (options.expandParent) {
          node.expandParents(node)
        }
      })
    },

    /**
     * @vuese
     * 选中/反选节点
     * @arg target 节点的key/data/node
     * @arg checked boolean 选中状态
     */
    setChecked(target, checked) {
      if (!this.hasNodeKey()) {
        return
      }
      const node = this.getNode(target)
      // const key = _.isObject(target) ? target[this.nodeKey] : target
      // const nodes = this.getNodesByKeys(this, [key])
      if (node) {
        if (checked) {
          node.checkNode(node)
        } else {
          node.uncheckNode(node)
        }
        node.traverse()
      }
    },
    /**
     * @vuese
     * 选择/反选节点
     * @arg target 节点的key/data，
     * @arg selected boolean 选择状态
     */
    setSelected(target, selected) {
      if (!this.hasNodeKey()) {
        return
      }
      const key = _.isObject(target) ? target[this.nodeKey] : target
      const data = this.getDataByKeys(this.data, [key])
      if (_.isEmpty(data)) {
        return
      }
      const targetData = data[0]
      if (selected) {
        this.selectedData = targetData
      } else {
        // 如果当前选中的节点是key指向的节点，反选之！
        if (this.selectedData === targetData) {
          this.selectedData = null
        }
      }
    },

    /**
     * @vuese
     * 获取select节点的data
     */
    getSelectedData() {
      return this.selectedData
    },

    /**
     * @vuese
     * 获取selected节点的key
     */
    getSelectedKey() {
      if (!this.hasNodeKey()) {
        return null
      }
      return this.selectedData ? this.selectedData[this.nodeKey] : null
    },

    /**
     * @vuese
     * 使用key选择节点
     * @arg key
     * @arg options Object: expand 展开节点，默认false，展开节点时默认会展开其所有父节点；expandParent 展开父节点，默认false
     */
    setSelectedKey(key, options = { expand: false, expandParent: false }) {
      const data = this.getDataByKeys(this.data, [key])
      this.selectedData = !_.isEmpty(data) ? data[0] : null
      const nodes = this.getNodesByKeys(this, [key])
      if (!_.isEmpty(nodes)) {
        const node = nodes[0]
        if (options.expand) {
          node.expandNode()
        }
        if (options.expandParent) {
          node.expandParents(node)
        }
      }
    },

    /**
     * @vuese
     * 同步子节点选中状态
     * @arg target 节点的key/data/node
     */
    traverseChildren(target) {
      if (!this.hasNodeKey()) {
        return
      }
      const node = this.getNode(target)
      if (node) {
        node.traverse('children')
      }
    },

    /**
     * @vuese
     * 同步父节点选中状态
     * @arg target 节点的key/data/node
     */
    traverseParents(target) {
      if (!this.hasNodeKey()) {
        return
      }
      const node = this.getNode(target)
      if (node) {
        node.traverse('parents')
      }
    },

    /**
     * @vuese
     * 获取父节点
     * @arg target 节点的key/data，
     */
    getParentNode(target) {
      if (!this.hasNodeKey()) {
        return null
      }
      const node = this.getNode(target)
      if (node) {
        return node.getParentTreeNode(node)
      }
      return null
    },

    /**
     * 触发check
     * @param value
     */
    triggerChecked(value) {
      // 节点check事件
      // @arg { data, node, checked }
      this.$emit('node-check', value)
    },
  },
  render() {
    const children = this.renderTreeNode(this.data)
    const css = {
      'km-tree': true,
      'km-tree--show-line': true,
    }

    const data = {
      class: css,
    }

    return (
      <div {...data}>
        {children.length > 0 ? children : this.$scopedSlots.empty}
      </div>
    )
  },
}
